import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PasswordSchema } from '@zevik/schemas';

/**
 * The password rule, said before submission and as a rule
 * (docs/DECISIONS.md ZD-118 §2).
 *
 * NIST SP 800-63B-4 §3.1.1.2: fifteen characters at least, no composition
 * rules, no expiry. The sentence a person reads is composed from the same
 * constant the API's schema refuses on, and the check run before a request
 * leaves the browser **is that schema** — `PasswordSchema.safeParse`, not a
 * second comparison — so the page cannot accept what the API refuses or
 * refuse what it accepts, and the sentence a refusal carries is the schema's
 * own (`packages/schemas/src/credential.ts`).
 *
 * A confirm field is deliberately absent. It catches a typo by making a
 * person type the password twice; a show-password control catches it by
 * letting them look, and the second is better when the rule is length-only
 * and the password may be pasted from a manager, where typing it twice is
 * pasting it twice.
 */

export const PASSWORD_RULE =
  `At least ${String(PASSWORD_MIN_LENGTH)} characters. Nothing else is required — no digits ` +
  `or symbols — and it will not expire.`;

/** The schema's own sentence where the password does not meet the rule; `null` where it does. */
export function passwordProblem(password: string): string | null {
  const parsed = PasswordSchema.safeParse(password);
  if (parsed.success) return null;
  return parsed.error.issues[0]?.message ?? 'the password does not meet the rule';
}

/**
 * The count a person watches while typing, against the rule's floor. Counted
 * the way the schema counts — `String.length` — so that the number on screen
 * and the number the API refuses on are one number.
 */
export function passwordProgress(password: string): { length: number; meets: boolean } {
  const length = password.length;
  return { length, meets: length >= PASSWORD_MIN_LENGTH && length <= PASSWORD_MAX_LENGTH };
}

export { PASSWORD_MIN_LENGTH };
